import { ApolloError, useMutation, FetchResult } from '@apollo/client';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DesignerQuery_designer as Designer } from '../../generated/types/DesignerQuery';
import { Gender } from '../../generated/types/globalTypes';
import { reportError } from '../../lib/reportError';
import { tc } from '../../lib/trackingContext';
import { useEmailVerification } from '../useEmailVerification';
import { useSessionQueryParams } from '../useSessionQueryParams';
import { EmailSubscribeMutation, EMAIL_SUBSCRIBE_MUTATION } from './useEmailSubscribe.rest';

export const useEmailSubscribe = () => {
  const { vertical = 'women' } = useParams<{
    vertical: Gender | undefined;
    trunkshowSlug: string | undefined;
    designerSlug: string | undefined;
  }>();

  const { isValid: isEmailValid, error: mailValidationError } = useEmailVerification();

  const [mutate, { loading, error }] = useMutation(EMAIL_SUBSCRIBE_MUTATION);

  const handleSuccess = useCallback(({ data }: FetchResult<EmailSubscribeMutation>) => {
    setIsSubscribed(true);
    setIsExistingSubscription(data?.emailSubscribe.table?.isExistingSubscription ?? false);
  }, []);

  const handleError = useCallback((caughtError: ApolloError) => reportError(caughtError), []);

  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isExistingSubscription, setIsExistingSubscription] = useState(false);
  const {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    params: { utm_source, utm_medium, utm_campaign, utm_content, utm_term }
  } = useSessionQueryParams();

  const subscribe = useCallback(
    (email: string, placement: 'footer' | 'emailPumoModule' | 'welcomePopup') => {
      if (!isEmailValid(email)) return;

      tc.track('Email Signup Submitted', {
        addToTrackingEvent: {
          placement,
          utm_source,
          utm_medium,
          utm_campaign,
          utm_content,
          utm_term
        }
      });

      mutate({
        variables: {
          input: {
            user: {
              email,
              gender: Gender[vertical]
            },
            placement
          }
        }
      })
        .then(handleSuccess)
        .catch(handleError);
    },
    [
      handleError,
      handleSuccess,
      isEmailValid,
      mutate,
      utm_campaign,
      utm_content,
      utm_medium,
      utm_source,
      utm_term,
      vertical
    ]
  );

  const subscribeToDesigner = useCallback(
    (email: string, designer: Designer) => {
      if (!isEmailValid(email)) return;

      mutate({
        variables: {
          input: {
            user: {
              email,
              gender: Gender[vertical]
            },
            placement: 'trunkshow',
            entity_id: Number(designer.id),
            entity_type: 'Trunkshow',
            trunkshow_preview: true
          }
        }
      })
        .then(handleSuccess)
        .catch(handleError);
    },
    [handleError, handleSuccess, isEmailValid, mutate, vertical]
  );

  return {
    subscribe,
    subscribeToDesigner,
    loading,
    error,
    mailValidationError,
    isSubscribed,
    isExistingSubscription
  };
};
